import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { BadgeCheck, Bell, Mail, Pencil, Phone, Users, type LucideIcon } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormProvider, type FieldValues } from 'react-hook-form';

import { queryKeys } from '@/lib/query-keys';
import { statusToneClasses } from '@/lib/statusTone';
import { cn } from '@/lib/utils';
import { Button, DialogClose } from '@/components/ui';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { useUnifiedForm } from '../../../hooks/useUnifiedForm';
import {
  personnelFormFields,
  personnelFormSchema,
  type PersonnelFormValues,
} from '../../companies/components/personnelFormSchema';
import { useCompanyPersonnel } from '@/features/companies/hooks/useCompanyPersonnel';
import { useCompany } from '@/features/companies/hooks';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import type { PersonRecord } from '@/data/schemas';
import { formatSiteShorthand } from '@/utils/entityIdentity';
import { useDataProvider } from '@/data/provider';

interface ReportPersonnelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string | null;
  companyName?: string | null;
  onCompanyUpdated?: (company: CompanyWithSites | null) => void;
  focusAlarmContact?: boolean;
}

export function ReportPersonnelModal({
  open,
  onOpenChange,
  companyId,
  companyName,
  onCompanyUpdated,
  focusAlarmContact = false,
}: ReportPersonnelModalProps): JSX.Element {
  const resolvedCompanyId = companyId ?? undefined;
  const { data: personnel = [], isLoading } = useCompanyPersonnel(resolvedCompanyId);
  const { data: company } = useCompany(resolvedCompanyId);
  const { contacts, companies } = useDataProvider();
  const [selectedPersonnelId, setSelectedPersonnelId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const siteOptions = useMemo(() => {
    const sites = company?.sites ?? [];
    return sites.map((site) => {
      const shorthand = formatSiteShorthand(company, site);
      return {
        value: site.id,
        label: shorthand ? `${site.name} (${shorthand})` : site.name,
      };
    });
  }, [company]);

  const { fields } = useUnifiedForm({
    schema: personnelFormSchema,
    fields: personnelFormFields,
    fieldOrder: [
      'firstName',
      'lastName',
      'role',
      'scope',
      'siteId',
      'email',
      'phone',
      'reportRecipient',
      'alarmContact',
      'reperibile',
      'fireTraining',
      'firstAidTraining',
      'licensed',
      'licenseExpiry',
      'licenseLastUpdate',
    ],
    fieldOverrides: {
      siteId: {
        options: siteOptions,
        disabled: (values: Record<string, unknown>) =>
          values.scope !== 'site' || siteOptions.length === 0,
        dependsOn: ['scope'],
      },
      licenseExpiry: {
        disabled: (values: Record<string, unknown>) => !Boolean(values.licensed),
        dependsOn: ['licensed'],
      },
      licenseLastUpdate: {
        disabled: (values: Record<string, unknown>) => !Boolean(values.licensed),
        dependsOn: ['licensed'],
      },
    },
  });

  const selectedPersonnel = useMemo(
    () => personnel.find((person) => person.id === selectedPersonnelId) ?? null,
    [personnel, selectedPersonnelId]
  );

  const selectedPersonnelFormData = useMemo(() => {
    if (!selectedPersonnel) return null;
    return {
      firstName: selectedPersonnel.firstName,
      lastName: selectedPersonnel.lastName,
      email: selectedPersonnel.email ?? undefined,
      phone: selectedPersonnel.phone ?? undefined,
      role: selectedPersonnel.role ?? '',
      scope: selectedPersonnel.scope ?? 'company',
      siteId:
        selectedPersonnel.scope === 'site' ? (selectedPersonnel.siteId ?? undefined) : undefined,
      reportRecipient: selectedPersonnel.reportRecipient,
      alarmContact: selectedPersonnel.alarmContact,
      fireTraining: selectedPersonnel.fireTraining,
      firstAidTraining: selectedPersonnel.firstAidTraining,
      licensed: selectedPersonnel.licensed,
      licenseExpiry: selectedPersonnel.licenseExpiry ?? undefined,
      licenseLastUpdate: selectedPersonnel.licenseLastUpdate ?? undefined,
      reperibile: selectedPersonnel.reperibile,
    } satisfies PersonnelFormValues;
  }, [selectedPersonnel]);

  // Form setup for create dialog
  const createForm = useEntityDialogForm({
    open: isCreateDialogOpen,
    schema: personnelFormSchema,
    fields,
    data: null,
  });

  // Form setup for edit dialog
  const editForm = useEntityDialogForm({
    open: Boolean(selectedPersonnel),
    schema: personnelFormSchema,
    fields,
    data: selectedPersonnelFormData,
  });

  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAutosaveRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedPersonnelFormData) {
      lastAutosaveRef.current = JSON.stringify(mapPersonnelFormValues(selectedPersonnelFormData));
    } else {
      lastAutosaveRef.current = null;
    }
  }, [selectedPersonnelFormData]);

  const handleAutoSave = useCallback(
    (values: FieldValues) => {
      if (!companyId || !selectedPersonnel) return;
      const payload = mapPersonnelFormValues(values as PersonnelFormValues);
      const snapshot = JSON.stringify(payload);
      if (snapshot === lastAutosaveRef.current) return;

      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
      autosaveTimeoutRef.current = setTimeout(async () => {
        try {
          await contacts.update(companyId, selectedPersonnel.id, payload);
          lastAutosaveRef.current = snapshot;
        } catch {
          // Silent auto-save failures; manual save still available.
        }
      }, 500);
    },
    [companyId, contacts, selectedPersonnel]
  );

  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);

  // Wire up autosave via form.watch for edit form
  useEffect(() => {
    if (!selectedPersonnel) return;
    const subscription = editForm.form.watch((values) => {
      handleAutoSave(values);
    });
    return () => {
      subscription.unsubscribe();
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [editForm.form, selectedPersonnel, handleAutoSave]);

  const invalidateQueries = async () => {
    if (!companyId) {
      return null;
    }

    const updatedCompany = await companies.getCompany(companyId);

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.detail(companyId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.personnel(companyId),
      }),
    ]);

    onCompanyUpdated?.(updatedCompany);
    return updatedCompany;
  };

  const createPersonnelMutation = useMutation({
    mutationFn: async (values: PersonnelFormValues) => {
      if (!companyId) {
        throw new Error('Nessuna azienda selezionata');
      }

      await contacts.add(companyId, mapPersonnelFormValues(values));
      return invalidateQueries();
    },
    onSuccess: () => {
      toast.success('Personale aggiunto');
      setIsCreateDialogOpen(false);
    },
    onError: () => {
      toast.error('Impossibile aggiungere il personale');
    },
  });

  const updatePersonnelMutation = useMutation({
    mutationFn: async (values: PersonnelFormValues) => {
      if (!companyId || !selectedPersonnel) {
        throw new Error('Personale non selezionato');
      }

      await contacts.update(companyId, selectedPersonnel.id, mapPersonnelFormValues(values));
      return invalidateQueries();
    },
    onSuccess: () => {
      toast.success('Personale aggiornato');
      setSelectedPersonnelId(null);
    },
    onError: () => {
      toast.error('Impossibile aggiornare il personale');
    },
  });

  const deletePersonnelMutation = useMutation({
    mutationFn: async (personId: string) => {
      if (!companyId) {
        throw new Error('Nessuna azienda selezionata');
      }

      await contacts.remove(companyId, personId);
      return invalidateQueries();
    },
    onSuccess: () => {
      toast.success('Personale eliminato');
      setSelectedPersonnelId(null);
    },
    onError: () => {
      toast.error('Impossibile eliminare il personale');
    },
  });

  const personnelCount = personnel.length;
  const companyDisplayName = companyName?.trim() || undefined;
  const alarmTone = statusToneClasses('warning');
  const scopeMessage = 'Le modifiche si applicheranno a questo report e a tutti i report futuri.';
  const contextMessage = companyDisplayName
    ? `Stai modificando il personale: ${companyDisplayName}`
    : 'Stai modificando il personale';
  const focusMessage = focusAlarmContact ? 'Contatti allarme in evidenza.' : null;

  const handleCreateSubmit = useEntityDialogSubmit({
    form: createForm.form,
    onSubmit: async (values: FieldValues) => {
      await createPersonnelMutation.mutateAsync(values as PersonnelFormValues);
    },
    setRootError: createForm.setRootError,
  });

  const handleEditSubmit = useEntityDialogSubmit({
    form: editForm.form,
    onSubmit: async (values: FieldValues) => {
      await updatePersonnelMutation.mutateAsync(values as PersonnelFormValues);
    },
    setRootError: editForm.setRootError,
  });

  const handleDelete = async () => {
    if (!selectedPersonnel) return;
    try {
      await deletePersonnelMutation.mutateAsync(selectedPersonnel.id);
    } catch (error) {
      editForm.setRootError('Impossibile eliminare. Riprova.');
      throw error;
    }
  };

  const sortedPersonnel = useMemo(() => {
    return [...personnel].sort((a, b) => {
      if (a.alarmContact !== b.alarmContact) {
        return a.alarmContact ? -1 : 1;
      }
      const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '', 'it-IT');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }
      return (a.firstName || '').localeCompare(b.firstName || '', 'it-IT');
    });
  }, [personnel]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectedPersonnelId(null);
            setIsCreateDialogOpen(false);
          }
          onOpenChange(nextOpen);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Personale aziendale</DialogTitle>
          </DialogHeader>

          <div className="pt-4">
            <Alert variant="info" showIcon>
              <AlertTitle>Contesto</AlertTitle>
              <AlertDescription>
                <p>{contextMessage}</p>
                <p>{scopeMessage}</p>
                {focusMessage ? <p>{focusMessage}</p> : null}
              </AlertDescription>
            </Alert>
          </div>

          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2">
              <Badge variant="info">{personnelCount} persone</Badge>
              {companyDisplayName ? (
                <span className="text-xs text-text-secondary">{companyDisplayName}</span>
              ) : null}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={!companyId}
            >
              Nuovo personale
            </Button>
          </div>

          {isLoading ? (
            <div className="rounded-lg border border-dashed border-[var(--border-subtle)] bg-surface p-6 text-center text-sm text-text-secondary">
              Caricamento personale...
            </div>
          ) : !companyId ? (
            <div className="rounded-lg border border-dashed border-[var(--border-subtle)] bg-surface p-6 text-center text-sm text-text-secondary">
              Seleziona un&#39;azienda per modificare il personale disponibile nel report.
            </div>
          ) : personnel.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[var(--border-subtle)] bg-surface p-10 text-center text-sm text-text-secondary">
              <Users className="h-12 w-12 text-text-tertiary" aria-hidden="true" />
              <p className="text-text-primary">
                Nessun personale registrato
                {companyDisplayName ? ` per ${companyDisplayName}` : ''}.
              </p>
              <Button variant="secondary" size="sm" onClick={() => setIsCreateDialogOpen(true)}>
                Aggiungi personale
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--border-subtle)] overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-surface">
              {sortedPersonnel.map((person) => {
                const fullName =
                  [person.firstName, person.lastName].filter(Boolean).join(' ').trim() ||
                  person.lastName ||
                  '—';
                const flags = buildPersonnelBadges(person);

                return (
                  <li
                    key={person.id}
                    className={cn(
                      'flex items-center justify-between gap-4 px-4 py-3',
                      person.alarmContact && alarmTone.subtleBackground
                    )}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text-primary">{fullName}</p>
                      <p className="truncate text-xs text-text-secondary">{person.role || '—'}</p>
                      {flags.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {flags.map((flag) => {
                            const Icon = flag.icon;
                            return (
                              <Badge
                                key={flag.label}
                                variant={flag.variant}
                                className="flex items-center gap-1"
                              >
                                <Icon className="h-3 w-3" aria-hidden="true" />
                                {flag.label}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedPersonnelId(person.id)}
                        aria-label={`Modifica ${fullName}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Personnel Dialog */}
      <EntityDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        title="Nuovo personale"
        size="sm"
        isDirty={createForm.form.formState.isDirty}
        isSaving={createPersonnelMutation.isPending}
        formId={createForm.formId}
        banner={createForm.rootError ? <FormErrorBanner message={createForm.rootError} /> : null}
        summary={
          createForm.errorCount > 0 ? (
            <FormErrorSummary
              count={createForm.errorCount}
              onJumpToFirstError={() => scrollToFirstError(createForm.form.formState.errors)}
            />
          ) : null
        }
        footer={
          <EntityDialogFooter
            secondary={
              <DialogClose asChild>
                <Button variant="ghost" disabled={createPersonnelMutation.isPending}>
                  Annulla
                </Button>
              </DialogClose>
            }
            primary={
              <Button
                type="submit"
                form={createForm.formId}
                disabled={createPersonnelMutation.isPending}
              >
                {createPersonnelMutation.isPending ? 'Aggiunta…' : 'Aggiungi personale'}
              </Button>
            }
          />
        }
      >
        <FormProvider {...createForm.form}>
          <form
            id={createForm.formId}
            onSubmit={createForm.form.handleSubmit(
              handleCreateSubmit,
              createForm.handleInvalidSubmit
            )}
            noValidate
            className="space-y-4"
          >
            <EntityFormFields fields={fields} disabled={createPersonnelMutation.isPending} />
          </form>
        </FormProvider>
      </EntityDialog>

      {/* Edit Personnel Dialog */}
      <EntityDialog
        open={Boolean(selectedPersonnel)}
        onOpenChange={(next) => {
          if (!next) {
            setSelectedPersonnelId(null);
          }
        }}
        title={
          selectedPersonnel ? `Modifica ${formatFullName(selectedPersonnel)}` : 'Modifica personale'
        }
        size="sm"
        isDirty={editForm.form.formState.isDirty}
        isSaving={updatePersonnelMutation.isPending}
        formId={editForm.formId}
        banner={editForm.rootError ? <FormErrorBanner message={editForm.rootError} /> : null}
        summary={
          editForm.errorCount > 0 ? (
            <FormErrorSummary
              count={editForm.errorCount}
              onJumpToFirstError={() => scrollToFirstError(editForm.form.formState.errors)}
            />
          ) : null
        }
        footer={
          <EntityDialogFooter
            extra={
              selectedPersonnel ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={updatePersonnelMutation.isPending || deletePersonnelMutation.isPending}
                >
                  {deletePersonnelMutation.isPending ? 'Eliminazione…' : 'Elimina contatto'}
                </Button>
              ) : null
            }
            secondary={
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  disabled={updatePersonnelMutation.isPending || deletePersonnelMutation.isPending}
                >
                  Annulla
                </Button>
              </DialogClose>
            }
            primary={
              <Button
                type="submit"
                form={editForm.formId}
                disabled={
                  !editForm.form.formState.isDirty ||
                  updatePersonnelMutation.isPending ||
                  deletePersonnelMutation.isPending
                }
              >
                {updatePersonnelMutation.isPending ? 'Salvataggio…' : 'Salva contatto'}
              </Button>
            }
          />
        }
      >
        <FormProvider {...editForm.form}>
          <form
            id={editForm.formId}
            onSubmit={editForm.form.handleSubmit(handleEditSubmit, editForm.handleInvalidSubmit)}
            noValidate
            className="space-y-4"
          >
            <EntityFormFields
              fields={fields}
              disabled={updatePersonnelMutation.isPending || deletePersonnelMutation.isPending}
            />
          </form>
        </FormProvider>
      </EntityDialog>
    </>
  );
}

function mapPersonnelFormValues(values: PersonnelFormValues) {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone,
    role: values.role,
    scope: values.scope,
    siteId: values.scope === 'site' ? (values.siteId ?? undefined) : undefined,
    reportRecipient: values.reportRecipient,
    alarmContact: values.alarmContact,
    fireTraining: values.fireTraining,
    firstAidTraining: values.firstAidTraining,
    licensed: values.licensed,
    licenseExpiry: values.licenseExpiry,
    licenseLastUpdate: values.licenseLastUpdate,
    reperibile: values.reperibile,
  };
}

function buildPersonnelBadges(person: PersonRecord) {
  const badges: Array<{
    label: string;
    icon: LucideIcon;
    variant: 'info' | 'warning' | 'success';
  }> = [];

  if (person.reportRecipient) {
    badges.push({ label: 'Verbali', icon: Mail, variant: 'info' });
  }
  if (person.alarmContact) {
    badges.push({
      label: 'Allarmi',
      icon: Bell,
      variant: 'warning',
    });
  }
  if (person.reperibile) {
    badges.push({
      label: 'Reperibile',
      icon: Phone,
      variant: 'warning',
    });
  }
  if (person.licensed) {
    badges.push({ label: 'Patentato', icon: BadgeCheck, variant: 'success' });
  }

  return badges;
}

function formatFullName(person: PersonRecord) {
  return (
    [person.firstName, person.lastName].filter(Boolean).join(' ').trim() ||
    person.lastName ||
    'personale'
  );
}
